import { memo, useState, useEffect, useRef } from 'react'
import { withRouter, useParams } from 'react-router-dom'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import { Box, Flex } from '@chakra-ui/react'
import DsrPageSideMenu from './DsrPageSideMenu'
import DisplayNewsFeedPublic from '../dsr-content-components/DisplayNewsFeedPublic'
import RenderSubpageContent from './RenderSubpageContent'
import DsrDynamicModal from '../messages/DsrDynamicModal'
import EngagementTimer from '../../global/components/timer/EngagementTimer'
import EmbeddedZoomMeetingDisplay from '../dsr-zoom/EmbeddedZoomMeetingDisplay'

import { logGuestDsrAnalytics } from '../../analytics/actions/dsr-analytics-actions'
import { getThisUserIdToken, isValidArray } from '../../global/helpers/global-helpers'
import useLaunchdeckModal from '../../global/hooks/use-launchdeck-modal'
import useChangeSubpageDisplay from '../../global/hooks/change-subpage-display'

import { manageDsrUrlTags } from '../../global/helpers/url-tag-helpers'
import { sendToGA } from '../../global/helpers/google-tag-helpers'

import '../../layout/components/layout/styles/dsr-content.scss'

// Note: Render Dsr Content for ACTIVE states
const RenderStaticDsrContent = ({
  dsrId,
  dsrConfiguration,
  dsrUserTeam,
  sessionId,
  selectedDisplaySubpage,
  selectedDisplayPageId,
  logGuestDsrAnalytics,
  userActionRequiredState,
  showDsrModalState,
  isArchivedDsr,
  dsrFeedContent,

  // Redux states
  isReadingMode,
  dsrPagesRedux,
  dsrPropertiesRedux,
  overviewPageId,
  showLoadMoreRedux,
  dsrActionList
}) => {

  const { pageIdToken } = useParams();
  const activeSecondsRef = useRef(0);
  const initialPageRef = useRef(false);

  const { openLaunchdeckModal, closeLaunchdeckModal } = useLaunchdeckModal();
  const { handleSelectSubpage, clickCount } = useChangeSubpageDisplay();

  const [localState, setLocalState] = useState({
    showDsrModalLoader: false,
    showConfirmationModal: false,
    confirmationType: '',
    postContentToEdit: '',
    replyIdToEdit: '',
    userIdToken: '',
    modalTransferPayload: '',
    postTagProperties: [],
  })
  const [displayedPostContent, setDisplayedPostContent]=useState([])

  
  const [extraHeaderLayoutOffset, setExtraHeaderLayoutOffset] = useState(false)
  const isGuestUser = (dsrUserTeam === "audience") ? true : false;

  // Reset timer
  const resetEngagementTimer = () => {
    activeSecondsRef.current = 0;
  };
  
  // Page view management
  const handleClickSubpageChange = (newSelectedDisplayPageId, newSelectedDisplayFixedSubpageType) => {
    handleSelectSubpage(newSelectedDisplayPageId, newSelectedDisplayFixedSubpageType)

    if (isGuestUser) {
      // For Sales intelligence
      logGuestDsrAnalytics({
        eventType: 'guest-views-subpage',
        analyticsPayload: {
          userId: getThisUserIdToken(),
          dsrId,
          sessionId: sessionId,
          engagementDurationSeconds: activeSecondsRef.current,
          viewedPageId: selectedDisplayPageId ? selectedDisplayPageId : selectedDisplaySubpage,
        }
      })

      resetEngagementTimer()
    }

    // For internal BI
    sendToGA('view_dsr_page', {
      'eventTimestamp': new Date().toISOString(),
      'dsrId': dsrId,
      'userIdToken': getThisUserIdToken(),
      'pageId': selectedDisplayPageId ? selectedDisplayPageId : selectedDisplaySubpage,
      'pageEngagementTimeSeconds': activeSecondsRef.current
    });
  }

  useEffect(() => {
    if (pageIdToken) {
      const pageId = 'pg__' + pageIdToken;
      handleClickSubpageChange(pageId, "")
    }
    else if (!pageIdToken && !initialPageRef.current) {
      // Default page
      const overviewPageId = dsrPagesRedux ? dsrPagesRedux[0].pageId : "";

      if (overviewPageId) {
        handleClickSubpageChange(overviewPageId, "")
        initialPageRef.current = true
      }
    }
  }, [pageIdToken, handleClickSubpageChange, dsrPagesRedux]);

  useEffect(() => {
    if (isArchivedDsr) {
      // For Archived DSRs
     isValidArray(dsrFeedContent) && setDisplayedPostContent(dsrFeedContent)
    } else {
      // For Static DSRs
      isValidArray(dsrConfiguration?.staticFeedContent.postArray) && setDisplayedPostContent(dsrConfiguration?.staticFeedContent.postArray)
    }
  }, [isArchivedDsr,
  dsrFeedContent,dsrConfiguration?.staticFeedContent?.postArray]);

  useEffect(() => {
    const handleUrlChange = () => {
      const viewpage = manageDsrUrlTags("viewpage");

      switch (viewpage) {
        case ('overview'): {
           // Overview page is called with pageId
          overviewPageId && handleClickSubpageChange(overviewPageId, "")
          break;
        }
        case ('updates'): {
          handleClickSubpageChange("", "updates-subpage")
          break;
        }
        case ('faq'): {
          handleSelectSubpage("","faq-subpage")
          break;
        }
        case ('map'): {
          handleClickSubpageChange("", "action-list-subpage")
          break;
        }
        case ('shared-docs'): {
          handleClickSubpageChange("", "shared-docs-folder-subpage")
          break;
        }
        default: {
          viewpage && handleClickSubpageChange("pg__" + viewpage, "")
          break;
        }
      }
    };

    window.addEventListener("url-changed", handleUrlChange);

    return () => {
      window.removeEventListener("url-changed", handleUrlChange);
    };

  }, [dsrPagesRedux]);

  useEffect(() => {
    if (dsrPropertiesRedux.logoDisplayOption) {
      if (dsrPropertiesRedux.logoDisplayOption === "default") {
        !!extraHeaderLayoutOffset && setExtraHeaderLayoutOffset(false)
      } else {
        !extraHeaderLayoutOffset && setExtraHeaderLayoutOffset(true)
      }
    }
  }, [dsrPropertiesRedux.logoDisplayOption]);

  // Analytics 
  useEffect(() => {
    // Register the duration in which the last subpage was viewed before user closes the window or exits the DSR
    const handleBeforeUnload = () => {

      logGuestDsrAnalytics({
        eventType: 'guest-views-subpage',
        analyticsPayload: {
          userId: getThisUserIdToken(),
          dsrId,
          sessionId: sessionId,
          engagementDurationSeconds: activeSecondsRef.current,
          viewedPageId: selectedDisplayPageId
        }
      })
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [logGuestDsrAnalytics, dsrId, activeSecondsRef.current, sessionId, selectedDisplayPageId])

  // UX Handlers
  const handleChangeUserActionRequired = (newUserActionRequired, modalTransferPayload) => {
    openLaunchdeckModal(newUserActionRequired)

    setLocalState((prevState) => ({
      ...prevState,
      modalTransferPayload,
    }))
  }


  const handleDsrModalClose = () => {
    closeLaunchdeckModal()

    setLocalState((prevState) => ({
      ...prevState,
      postIdToEdit: '',
    }))
  }


  const handleOpenSignupOrLoginModal = () => {
    openLaunchdeckModal("show-signup-login-prompt")
  }


  return (
    <Flex justifyContent={['flex-start', 'center']} className="dsr-content__section">
      {/* // Engagement Time is meant to be hidden and has no displayed UI */}
      {isGuestUser &&
        <EngagementTimer
        updateActiveSecondsRef={activeSecondsRef} 
        resetTimer={resetEngagementTimer}
        />
      }
      <Flex className="dsr-content__container">
        <Flex direction="column" align="flex-start" w="100%" height='100%'>
          <Flex direction="row" align="flex-start" width="100%">
            <Flex display={['none', 'flex']} position="sticky" top='70px' bg="white">
              <DsrPageSideMenu
                pageArray={dsrPagesRedux}
                selectedDisplayPageId={selectedDisplayPageId}
                handleSelectSubpage={handleClickSubpageChange}
                extraHeaderLayoutOffset={extraHeaderLayoutOffset}
                altPageNames={dsrPropertiesRedux.altPageNames}
                headerMenuHeight="70px"
              />
            </Flex>
            <Flex
              direction={['column', 'column', 'column', 'row']}
              justify="center"
              flexWrap="wrap"
              width="100%"
              background="#F4F4F4"
              h='auto'
              minH='100vh'
            >
              <Box
                key={'page-view-' + clickCount}
                className="dsr-content__middle-column-container fade-in"
                transition="max-width 0.75s ease-in-out"
                maxWidth={isReadingMode ? '1200px' : ['100%', '800px']}
                pt={extraHeaderLayoutOffset && '1em'}
                px={isReadingMode ? "5em" : "1em"}
                w='100%'
              >
                {selectedDisplaySubpage === 'updates-subpage' ?

                  <DisplayNewsFeedPublic
                    dsrId={dsrId}
                    dsrTitle={dsrConfiguration?.dsrTitle}
                    // displayedPostContent={dsrConfiguration?.staticFeedContent.postArray ? dsrConfiguration.staticFeedContent.postArray : []}
                    displayedPostContent={displayedPostContent}
                    dsrTeamMembers={dsrConfiguration?.dsrTeamMembers}
                    showDsrModal={showDsrModalState}
                    showDsrModalLoader={localState.showDsrModalLoader}
                    showLoadMore={showLoadMoreRedux}
                    modalTransferPayload={localState.modalTransferPayload}
                    handleChangeUserActionRequired={handleChangeUserActionRequired}
                    handleDsrModalClose={handleDsrModalClose}
                    handleSelectSubpage={handleClickSubpageChange}
                    handleOpenSignupOrLoginModal={handleOpenSignupOrLoginModal}
                    isArchivedDsr={isArchivedDsr}
                  />

                  :

                  <RenderSubpageContent
                    // /* Dev Note: This component is to display static public content, sharing RenderSubpageContent with the normal DSR display */
                    pageArray={dsrPagesRedux}
                    selectedDisplaySubpage={selectedDisplaySubpage}
                    selectedDisplayPageId={selectedDisplayPageId}
                    dsrTeamMembers={dsrConfiguration?.dsrTeamMembers}
                    dsrId={dsrId}
                    dsrActionList={isArchivedDsr ? dsrActionList || [] : []}
                    dsrProperties={dsrPropertiesRedux}
                    handleOpenSignupOrLoginModal={handleOpenSignupOrLoginModal}
                    handleSelectSubpage={handleClickSubpageChange}
                    isArchivedDsr={isArchivedDsr}
                  />
                }
                <EmbeddedZoomMeetingDisplay />
              </Box>
            </Flex>

            {/* // This section houses dynamically rendered components that depends on user journey */}


            <DsrDynamicModal
              dsrId={dsrId}
              userActionRequired={userActionRequiredState}
              showDsrModal={showDsrModalState}
              showDsrModalLoader={localState.showDsrModalLoader}
              handleDsrModalClose={handleDsrModalClose}
              postTagProperties={localState.postTagProperties}
              handleChangeUserActionRequired={handleChangeUserActionRequired}
              modalTransferPayload={localState.modalTransferPayload}
            />

          </Flex>
        </Flex>
      </Flex>
    </Flex>
  )

}

const mapStateToProps = (state) => {
  return {
    userActionRequiredState: state.dsrPopup.UserActionRequired,
    showDsrModalState: state.dsrModal.showDsrModal,
    selectedDisplaySubpage: state.changeDsrPage.selectedDisplaySubpage,
    selectedDisplayPageId: state.changeDsrPage.selectedDisplayPageId,
    dsrPagesRedux: state.setDsrPages.pages,
    isReadingMode: state.isReadingMode.results,
    dsrPropertiesRedux: state.draftDsrProperties.properties,
    dsrTeamMembersRedux: state.dsrTeamMembers.members,
    dsrUserTeam: state.dsrUserTeam.userTeam,
	  overviewPageId: state.overviewPageId.id,
    showLoadMoreRedux: state.dsrFeedContent.allowLoadMore,
    dsrActionList: state.dsrActionList.results.data,
  }
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      logGuestDsrAnalytics,
    },
    dispatch,
  )

export default memo(withRouter(connect(mapStateToProps, mapDispatchToProps)(RenderStaticDsrContent)))
